import React, { useState } from "react";
import HoverIconButton from "components/HoverIconButton";
import Button from "components/Form/fields/Button";
import { useFormikContext } from "formik";
import get from "lodash/get";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import { STATE_KEYS } from "utils/constants/state";
import { REACT_KEY, SMALL_ICON_BUTTON_PROPS } from "utils/constants/ui";
import { FIELD_GROUP_INTENT } from "utils/constants/fields";
import { useHoverProps } from "utils/hooks";

const DEFAULT_CLASSES = {
  container:
    "flex flex-column justify-between items-end sticky-l bg-white bh--inset top-0 z-5",

  navBar: "w-100 flex flex-row bb hairline-1 ph1",
  content: "pb3 ph3 pl1 bb hairline-1 pt3 w-100 flex flex-row items-center",
  label: "fw6 f4 word-break",
  buttonWrapper: "flex w-100 items-center",
  actionWrapper: "flex flex-shrink-0"
};

const applyFocus = (values, focus) => {
  focus.forEach(({ key, value }) => set(values, key, value));
  return values;
};

const NAV_ICON = {
  BACK: "back",
  FALLBACK: "fallback"
};

const UNSET = {
  MENU_INTENT: {
    key: STATE_KEYS.EDITOR.MENU_INTENT,
    value: null
  },
  MENU_SUB_INTENT: {
    key: STATE_KEYS.EDITOR.MENU_SUB_INTENT,
    value: null
  },
  MENU_THIRD_INTENT: {
    key: STATE_KEYS.EDITOR.MENU_THIRD_INTENT,
    value: null
  },
  MENU_ACTIVE_INDEX: {
    key: STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX,
    value: null
  },
  MENU_ACTIVE_SUB_INDEX: {
    key: STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX,
    value: null
  }
};

const BackNav = ({ type, node, nodeIx, isLast }) => {
  /**
   * On hover we switch the fallback icon to be a back icon
   * - this represents a double back action
   */
  const [hover, setHover] = useState(false);
  const hoverProps = useHoverProps({ hover, setHover });
  const ctxType = hover && type === NAV_ICON.FALLBACK ? NAV_ICON.BACK : type;

  return (
    <div className="dib flex flex-row items-center" {...hoverProps}>
      <HoverIconButton type={ctxType} {...node} key={nodeIx} />
      {!isLast && <div className="f5">/</div>}
    </div>
  );
};

const MenuNavHeader = ({
  label,
  action,
  children,
  footer,
  navSetters,
  customClasses
}) => {
  const { values, setValues } = useFormikContext();
  const classes = { ...DEFAULT_CLASSES, ...customClasses };

  const menuIntent = get(values, STATE_KEYS.EDITOR.MENU_INTENT);
  const menuSubIntent = get(values, STATE_KEYS.EDITOR.MENU_SUB_INTENT);
  const menuThirdIntent = get(values, STATE_KEYS.EDITOR.MENU_THIRD_INTENT);
  const isAddFieldIntent = menuSubIntent === FIELD_GROUP_INTENT.ADD_FIELD;

  const activeIndex = get(values, STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX);
  const activeSubIndex = get(values, STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX);
  // Log out nav fields
  // console.table({
  //   menuIntent,
  //   menuSubIntent,
  //   activeIndex,
  //   activeSubIndex
  // });

  const hasActiveIndex = Number.isInteger(activeIndex);
  const hasActiveSubIndex = Number.isInteger(activeSubIndex);
  let nodes = [];

  /**
   * The root level menu intent e.g
   * FIELD_GROUP_ORDER - Index of field groups
   * All types are listed under FORM_INTENT
   */
  if (menuIntent) {
    nodes.push({
      onClick: () => {
        const updatedValues = cloneDeep(values);
        const focus = [UNSET.MENU_INTENT];

        if (menuSubIntent) {
          focus.push(UNSET.MENU_SUB_INTENT);
        }
        if (menuThirdIntent) {
          focus.push(UNSET.MENU_THIRD_INTENT);
        }
        if (hasActiveIndex) {
          focus.push(UNSET.MENU_ACTIVE_INDEX);
        }
        if (hasActiveSubIndex) {
          focus.push(UNSET.MENU_ACTIVE_SUB_INDEX);
        }

        setValues(applyFocus(updatedValues, focus));
      },
      ...SMALL_ICON_BUTTON_PROPS
    });
  }

  /**
   * The sub level menu intent
   * menuIntent: CHECKOUT_SETTINGS
   * menuSubIntent: SET_CONSTANT | SET_PRODUCTS | SET_COMMON
   */
  if (menuSubIntent) {
    nodes.push({
      onClick: () => {
        const updatedValues = cloneDeep(values);
        const focus = [UNSET.MENU_SUB_INTENT];

        if (hasActiveIndex) {
          focus.push(UNSET.MENU_ACTIVE_INDEX);
        }
        if (hasActiveSubIndex) {
          focus.push(UNSET.MENU_ACTIVE_SUB_INDEX);
        }

        setValues(applyFocus(updatedValues, focus));
      },
      ...SMALL_ICON_BUTTON_PROPS
    });
  }

  /**
   * The third level menu intent
   * menuIntent: CHECKOUT_SETTINGS
   * menuSubIntent: SET_CONSTANT | SET_PRODUCTS | SET_COMMON
   * menuThirdIntent: SET_FIELDS | SET_COMBINATIONS | SET_SETTINGs
   */
  if (menuThirdIntent) {
    nodes.push({
      onClick: () => {
        const updatedValues = cloneDeep(values);
        const focus = [UNSET.MENU_THIRD_INTENT];

        if (hasActiveIndex) {
          focus.push(UNSET.MENU_ACTIVE_INDEX);
        }
        if (hasActiveSubIndex) {
          focus.push(UNSET.MENU_ACTIVE_SUB_INDEX);
        }

        setValues(applyFocus(updatedValues, focus));
      },
      ...SMALL_ICON_BUTTON_PROPS
    });
  }

  /**
   * These represent the nested indexed item menus like fieldGroups[0] etc
   * Are generic in that they are based on index and not key name so can be re-used across different lists
   *
   * Example
   * - field group show - fieldGroups[0]
   * - active index is 0
   */
  if (hasActiveIndex) {
    nodes.push({
      onClick: () => {
        const updatedValues = cloneDeep(values);
        const focus = [];

        if (isAddFieldIntent) {
          focus.push(UNSET.MENU_SUB_INTENT);
        } else {
          focus.push(UNSET.MENU_ACTIVE_INDEX);
        }

        if (hasActiveSubIndex) {
          focus.push(UNSET.MENU_ACTIVE_SUB_INDEX);
        }
        setValues(applyFocus(updatedValues, focus));
      },
      ...SMALL_ICON_BUTTON_PROPS
    });
  }

  /**
   * Example
   * - field is showing - fieldGroups[0].fields[1]
   * - active sub index is 1
   * Note:
   * - When adding a field it acts as an "overlay" (menuSubIntent === FIELD_GROUP_INTENT.ADD_FIELD)
   * - i.e. there could be an existing field showing
   * - in that case we want to remove the add field "overlay" by setting the menuSubIntent to null AND we don not unset the active field index (ACTIVE_SUB_INDEX)
   */
  if (hasActiveSubIndex) {
    nodes.push({
      onClick: () => {
        const updatedValues = cloneDeep(values);
        let focus = [];

        if (isAddFieldIntent) {
          focus.push(UNSET.MENU_SUB_INTENT);
        } else {
          focus.push(UNSET.MENU_ACTIVE_SUB_INDEX);
        }

        setValues(applyFocus(updatedValues, focus));
      },
      ...SMALL_ICON_BUTTON_PROPS
    });
  }

  /**
   * Currently passed in as the back button from the consumer so its the "last" item
   */
  if (navSetters) {
    navSetters.forEach((setter) =>
      nodes.push({
        ...setter,
        ...SMALL_ICON_BUTTON_PROPS
      })
    );
  }

  const hasContent = Boolean(label || action || children);

  return (
    <div id="menu--navHeader" className={classes.container}>
      {nodes.length > 0 && (
        <div className={classes.navBar}>
          {nodes.map((node, nodeIx) => {
            const isLast = nodeIx === nodes.length - 1;
            const type = isLast ? NAV_ICON.BACK : NAV_ICON.FALLBACK;
            return (
              <BackNav
                key={nodeIx}
                nodeIx={nodeIx}
                isLast={isLast}
                node={node}
                type={type}
              />
            );
          })}
        </div>
      )}
      {hasContent && (
        <div className={classes.content}>
          <div className={classes.buttonWrapper}>
            {label && <div className={classes.label}>{label}</div>}
          </div>
          {action ? (
            action[REACT_KEY] ? (
              action
            ) : (
              <div className={classes.actionWrapper}>
                <Button theme="slim" {...action} />
              </div>
            )
          ) : null}
          {children && <div className={classes.actionWrapper}>{children}</div>}
        </div>
      )}
      {footer}
    </div>
  );
};

export default MenuNavHeader;
